/**
 * Documentation Service - Loads static documentation content
 *
 * This service handles:
 * - Loading the documentation navigation manifest (index.json)
 * - Loading individual documentation files by path
 * - Providing the base URL for documentation files
 *
 * Context: AC-658 - Create docsService and docsSearchService
 *
 * Technical Design:
 * - Uses fetch API to load from /docs/ directory
 * - Returns DocCategory[] for navigation, string for document content
 */

import { DocCategory } from '../types/help';

/** Base URL for documentation files */
const DOCS_BASE_URL = '/docs';

/**
 * Strip YAML frontmatter from markdown content.
 * Frontmatter is delimited by --- at the start of the file.
 * @param content - Raw markdown content that may include frontmatter
 * @returns Markdown content with frontmatter removed
 */
const stripFrontmatter = (content: string): string => {
  // Check if content starts with frontmatter delimiter
  if (!content.startsWith('---')) {
    return content;
  }

  // Find the closing delimiter (second ---)
  const endIndex = content.indexOf('---', 3);
  if (endIndex === -1) {
    return content; // No closing delimiter, return as-is
  }

  // Return content after frontmatter, trimming leading whitespace
  return content.slice(endIndex + 3).trimStart();
};

/**
 * Documentation service interface
 */
interface DocsService {
  /**
   * Load the documentation navigation manifest (index.json)
   */
  loadNavigation(): Promise<DocCategory[]>;

  /**
   * Load a specific documentation file by path
   */
  loadDocument(docPath: string): Promise<string>;

  /**
   * Get the base URL for documentation files
   */
  getDocsBaseUrl(): string;
}

/**
 * Interface for the navigation manifest JSON structure
 */
interface NavigationManifest {
  version: string;
  categories: DocCategory[];
}

const docsService: DocsService = {
  /**
   * Load the documentation navigation manifest from /docs/index.json
   * @returns Promise resolving to an array of DocCategory objects
   * @throws Error if fetch fails or response is not ok
   */
  loadNavigation: async (): Promise<DocCategory[]> => {
    const response = await fetch(`${DOCS_BASE_URL}/index.json`);

    if (!response.ok) {
      throw new Error(`Failed to load navigation: ${response.status} ${response.statusText}`);
    }

    const manifest: NavigationManifest = await response.json();
    return manifest.categories;
  },

  /**
   * Load a specific documentation file by path
   * @param docPath - The relative path to the document (e.g., 'getting-started/welcome.md')
   * @returns Promise resolving to the markdown content (with frontmatter stripped) as a string
   * @throws Error if fetch fails or response is not ok
   */
  loadDocument: async (docPath: string): Promise<string> => {
    const response = await fetch(`${DOCS_BASE_URL}/${docPath}`);

    if (!response.ok) {
      throw new Error(`Failed to load document: ${response.status} ${response.statusText}`);
    }

    const rawContent = await response.text();
    return stripFrontmatter(rawContent);
  },

  /**
   * Get the base URL for documentation files
   * @returns The base URL string (without trailing slash)
   */
  getDocsBaseUrl: (): string => {
    return DOCS_BASE_URL;
  }
};

export default docsService;
